import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { automatisationApi } from '../api/automatisation.api';
import { AutomationFormData } from '../types/automatisation.types';
import { useAppStore } from '@/app/store';

export const useAutomatisations = () => {
  const queryClient = useQueryClient();
  const addNotification = useAppStore(state => state.addNotification);

  // Fetch all automations
  const { data: automations = [], isLoading, error } = useQuery({
    queryKey: ['admin-automatisations'],
    queryFn: automatisationApi.getAllAutomations,
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  // Fetch automation stats
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['admin-automatisation-stats'],
    queryFn: automatisationApi.getAutomationStats,
    refetchInterval: 60000 // Refresh every minute
  });

  // Create automation mutation
  const createMutation = useMutation({
    mutationFn: automatisationApi.createAutomation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-automatisations'] });
      queryClient.invalidateQueries({ queryKey: ['admin-automatisation-stats'] });
      addNotification({
        type: 'success',
        title: 'Succès',
        message: 'Automatisation créée avec succès'
      });
    },
    onError: (error: any) => {
      addNotification({
        type: 'error',
        title: 'Erreur',
        message: error.response?.data?.message || 'Erreur lors de la création'
      });
    }
  });

  // Update automation mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AutomationFormData> }) => 
      automatisationApi.updateAutomation(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-automatisations'] });
      addNotification({
        type: 'success',
        title: 'Succès',
        message: 'Automatisation mise à jour avec succès'
      });
    },
    onError: (error: any) => {
      addNotification({
        type: 'error',
        title: 'Erreur',
        message: error.response?.data?.message || 'Erreur lors de la mise à jour'
      });
    }
  });

  // Delete automation mutation
  const deleteMutation = useMutation({
    mutationFn: automatisationApi.deleteAutomation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-automatisations'] });
      queryClient.invalidateQueries({ queryKey: ['admin-automatisation-stats'] });
      addNotification({
        type: 'success',
        title: 'Succès',
        message: 'Automatisation supprimée avec succès'
      });
    },
    onError: (error: any) => {
      addNotification({
        type: 'error',
        title: 'Erreur',
        message: error.response?.data?.message || 'Erreur lors de la suppression'
      });
    }
  });

  // Toggle automation mutation
  const toggleMutation = useMutation({
    mutationFn: automatisationApi.toggleAutomation,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-automatisations'] });
      addNotification({
        type: 'success',
        title: 'Succès',
        message: data.status === 'active'
          ? 'Automatisation activée'
          : 'Automatisation mise en pause'
      });
    },
    onError: (error: any) => {
      addNotification({
        type: 'error',
        title: 'Erreur',
        message: error.response?.data?.message || 'Erreur lors du changement de statut'
      });
    }
  });

  // Execute automation mutation (manual trigger)
  const executeMutation = useMutation({
    mutationFn: automatisationApi.executeAutomation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-automatisations'] });
      queryClient.invalidateQueries({ queryKey: ['admin-automatisation-logs'] });
      addNotification({
        type: 'success',
        title: 'Succès',
        message: 'Automatisation exécutée avec succès'
      });
    },
    onError: (error: any) => {
      addNotification({
        type: 'error',
        title: 'Erreur',
        message: error.response?.data?.message || 'Erreur lors de l\'exécution'
      });
    }
  });

  return {
    automations,
    stats,
    isLoading,
    statsLoading,
    error,
    createAutomation: createMutation.mutate,
    updateAutomation: updateMutation.mutate,
    deleteAutomation: deleteMutation.mutate,
    toggleAutomation: toggleMutation.mutate,
    executeAutomation: executeMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isToggling: toggleMutation.isPending,
    isExecuting: executeMutation.isPending
  };
};

export const useAutomationLogs = (automationId: string, enabled: boolean = false) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-automatisation-logs', automationId],
    queryFn: () => automatisationApi.getAutomationLogs(automationId),
    enabled: enabled && !!automationId,
    refetchInterval: 10000 // Refresh every 10 seconds when viewing logs
  });

  return {
    logs: data?.logs || [],
    total: data?.total || 0,
    isLoading,
    error,
    refetch
  };
};